import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Copy, Trash2 } from "lucide-react";
import api, { apiError, API } from "@/lib/api";
import { AdminHeader, Panel, EmptyState } from "@/components/admin/ui";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/perms";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function Media() {
  const { user } = useAuth();
  const canEdit = canAccess(user?.role, "media");
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post("/media", fd); toast.success("Uploaded"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = async () => {
    try { await api.delete(`/media/${confirm.id}`); toast.success("Deleted"); setConfirm(null); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const fullUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);
  const filtered = items.filter((i) => i.filename?.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <AdminHeader title="Media Library" subtitle="Upload and manage images. JPG, PNG, WEBP, GIF, SVG up to 5MB."
        action={canEdit && (
          <>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="media-file-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-gold px-4 py-2.5 text-sm inline-flex items-center gap-2 disabled:opacity-60" data-testid="media-upload-btn">
              <Upload size={16} /> {uploading ? "Uploading…" : "Upload"}
            </button>
          </>
        )} />

      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search files…" className="w-full sm:max-w-xs bg-[#06122E] border border-[#203E70] rounded px-3 py-2.5 text-sm text-white mb-6 outline-none focus:border-[#D8AE4E]/50" data-testid="media-search" />

      {filtered.length === 0 ? <Panel><EmptyState>No media uploaded yet.</EmptyState></Panel> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {filtered.map((m) => (
            <div key={m.id} className="cb-card-dark overflow-hidden group" data-testid={`media-item-${m.id}`}>
              <div className="aspect-square bg-[#06122E] flex items-center justify-center overflow-hidden">
                <img src={fullUrl(m.url)} alt={m.filename} className="w-full h-full object-cover" />
              </div>
              <div className="p-3">
                <p className="text-xs text-white truncate">{m.filename}</p>
                <p className="text-[10px] text-slate-500 font-mono">{(m.size / 1024).toFixed(0)} KB</p>
                <div className="flex gap-1 mt-2">
                  <button onClick={() => { navigator.clipboard.writeText(fullUrl(m.url)); toast.success("URL copied"); }} className="flex-1 btn-outline-gold py-1.5 text-xs inline-flex items-center justify-center gap-1" data-testid={`media-copy-${m.id}`}><Copy size={12} /> Copy</button>
                  {canEdit && <button onClick={() => setConfirm(m)} className="px-2 text-slate-400 hover:text-red-400" data-testid={`media-delete-${m.id}`}><Trash2 size={14} /></button>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0B1D40] border-[#203E70] text-white">
          <AlertDialogHeader><AlertDialogTitle>Delete media?</AlertDialogTitle><AlertDialogDescription className="text-slate-400">"{confirm?.filename}" will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#203E70] text-white hover:bg-[#102653]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700 text-white" data-testid="media-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
